import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, Building2, User, Mail, Phone, Users2, Factory, ClipboardCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ContactRequestDialog — Public "Get started" contact form for hubcmd.com.
 * Captures qualified lead data and emails support@trupathadvisory.com.
 */
export default function ContactRequestDialog({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", position: "", email: "", phone: "",
    company: "", company_size: "", industry: "", standards: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) {
      return toast.error("Name, company and email are required.");
    }
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/contact`, form);
      toast.success(`Request received — reference ${r.data?.display_id || ""}`);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed — please try again.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setForm({ name: "", position: "", email: "", phone: "", company: "", company_size: "", industry: "", standards: "", message: "" });
    setDone(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-[#ff4500]" /> Request access · Talk to us
          </DialogTitle>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="contact-done">
            <div className="w-16 h-16 rounded-full bg-[#1a8754] mx-auto flex items-center justify-center">
              <ClipboardCheck className="w-8 h-8 text-white" />
            </div>
            <div className="font-display font-black text-lg mt-4">Thanks — we'll be in touch.</div>
            <p className="text-sm text-[#1a1a1a]/65 mt-2 max-w-md mx-auto">
              Your request has been forwarded to TruPath Advisory. Expect a reply within one business day.
            </p>
            <button data-testid="contact-close" onClick={reset} className="btn-pill btn-pill-primary mt-6 text-[11px]">
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-[13px] text-[#1a1a1a]/65 mb-2">
              hubCMD is enterprise-onboarded — every org gets a dedicated Controller seat. Tell us a bit about your operation
              so we can prepare the right bundle and standards library for you.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Your name *" icon={User}>
                <input data-testid="contact-name" value={form.name} onChange={set("name")} className={fieldCls} />
              </Field>
              <Field label="Your position">
                <input data-testid="contact-position" value={form.position} onChange={set("position")} placeholder="e.g. QA Manager" className={fieldCls} />
              </Field>
              <Field label="Email *" icon={Mail}>
                <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={fieldCls} />
              </Field>
              <Field label="Phone" icon={Phone}>
                <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={fieldCls} />
              </Field>
              <Field label="Company *" icon={Building2}>
                <input data-testid="contact-company" value={form.company} onChange={set("company")} className={fieldCls} />
              </Field>
              <Field label="Approx. company size" icon={Users2} help="Helps size the number of seats needed">
                <select data-testid="contact-size" value={form.company_size} onChange={set("company_size")} className={fieldCls}>
                  <option value="">Select…</option>
                  <option value="1-10">1 – 10 employees</option>
                  <option value="11-50">11 – 50 employees</option>
                  <option value="51-200">51 – 200 employees</option>
                  <option value="201-500">201 – 500 employees</option>
                  <option value="501-1000">501 – 1,000 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </Field>
              <Field label="Industry" icon={Factory}>
                <input data-testid="contact-industry" value={form.industry} onChange={set("industry")} placeholder="e.g. Food manufacturing" className={fieldCls} />
              </Field>
              <Field label="Standards / 3rd-party audits">
                <input data-testid="contact-standards" value={form.standards} onChange={set("standards")}
                  placeholder="ISO 9001, SQF, HACCP, BRC…" className={fieldCls} />
              </Field>
            </div>
            <Field label="Anything else we should know?">
              <textarea data-testid="contact-message" rows={3} value={form.message} onChange={set("message")}
                placeholder="Timeline, key pain points, existing tooling…" className={`${fieldCls} resize-none`} />
            </Field>
            <div className="text-[10px] font-mono text-[#1a1a1a]/50 mt-2">
              By submitting, you consent to hubCMD forwarding these details to TruPath Advisory for follow-up.
            </div>
            <DialogFooter>
              <button onClick={reset} className="btn-pill btn-pill-ghost text-[11px]">Cancel</button>
              <button data-testid="contact-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary text-[11px]">
                {busy ? "Sending…" : "Send request"} <ArrowRight className="w-3 h-3" />
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, icon: Icon, help, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
        {Icon && <Icon className="w-3 h-3 inline mr-1 opacity-70" />}{label}
      </label>
      {children}
      {help && <div className="text-[10px] text-[#1a1a1a]/50 mt-1">{help}</div>}
    </div>
  );
}

const fieldCls = "w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15";
